import { type ChangeEvent, type DragEvent, useMemo, useRef, useState } from 'react'
import {
  AlertCircle,
  Download,
  Film,
  Image as ImageIcon,
  Loader2,
  Pencil,
  Search,
  Trash2,
  UploadCloud,
} from 'lucide-react'
import type { MediaItem } from '@/lib/types'
import { useStore } from '@/lib/store'
import { uploadMediaFile } from '@/lib/data'
import { PageHeader } from '@/components/PageHeader'

type FilterKey = 'all' | 'image' | 'video'

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'image', label: 'Images' },
  { key: 'video', label: 'Videos' },
]

function isRealUrl(url: string): boolean {
  return url.trim().length > 0
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts
  const min = Math.round(diff / 60000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.round(hr / 24)
  if (day < 30) return `${day}d ago`
  return new Date(ts).toLocaleDateString()
}

export function MediaPage() {
  const media = useStore((s) => s.media)
  const insertMedia = useStore((s) => s.insertMedia)
  const updateMedia = useStore((s) => s.updateMedia)
  const removeMedia = useStore((s) => s.removeMedia)

  const [query, setQuery] = useState<string>('')
  const [filter, setFilter] = useState<FilterKey>('all')
  const [dragging, setDragging] = useState<boolean>(false)
  const [uploading, setUploading] = useState<number>(0)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const counts = useMemo(() => {
    const images = media.filter((m) => m.type === 'image').length
    const videos = media.filter((m) => m.type === 'video').length
    return { total: media.length, images, videos }
  }, [media])

  const filtered = useMemo<MediaItem[]>(() => {
    const q = query.trim().toLowerCase()
    return media.filter((m) => {
      if (filter !== 'all' && m.type !== filter) return false
      if (q && !m.name.toLowerCase().includes(q)) return false
      return true
    })
  }, [media, query, filter])

  const openFilePicker = () => fileInputRef.current?.click()

  const ingestFiles = (files: FileList | File[]) => {
    Array.from(files).forEach(async (file) => {
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) return
      setUploadError(null)
      setUploading((n) => n + 1)
      try {
        // Real upload: bytes go to R2 via the worker, and the returned item
        // carries a public URL the publish engine can deliver to networks.
        insertMedia(await uploadMediaFile(file))
      } catch {
        setUploadError('Upload failed. Check storage setup and try again.')
      } finally {
        setUploading((n) => n - 1)
      }
    })
  }

  const onFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length) ingestFiles(e.target.files)
    e.target.value = ''
  }

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length) ingestFiles(e.dataTransfer.files)
  }

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (!dragging) setDragging(true)
  }

  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(false)
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Media"
        subtitle="Your image & video library"
        icon={<ImageIcon size={20} />}
        actions={
          <button className="btn-primary" onClick={openFilePicker} disabled={uploading > 0}>
            {uploading > 0 ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
            {uploading > 0 ? 'Uploading...' : 'Upload'}
          </button>
        }
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={onFileInput}
      />

      <div className="flex-1 overflow-y-auto p-6">
        {uploadError && (
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-accent-red/40 bg-accent-red/10 p-3 text-sm text-accent-red">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{uploadError}</span>
          </div>
        )}
        {media.length === 0 ? (
          <div className="grid min-h-[60vh] place-items-center animate-slide-up">
            <div
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={openFilePicker}
              className={[
                'flex w-full max-w-lg cursor-pointer flex-col items-center gap-4 rounded-2xl border-2 border-dashed px-8 py-14 text-center transition-colors',
                dragging
                  ? 'border-brand-400 bg-brand-50'
                  : 'border-line bg-panel hover:border-brand-400/60 hover:bg-surface',
              ].join(' ')}
            >
              <span className="grid h-16 w-16 place-items-center rounded-2xl bg-brand-50 text-brand-500">
                <UploadCloud size={28} />
              </span>
              <div>
                <p className="font-display text-lg font-bold text-ink-900">
                  Drop files or click to upload
                </p>
                <p className="mt-1 text-sm text-ink-500">
                  PNG, JPG, MP4 up to ~10MB. Drag &amp; drop supported.
                </p>
              </div>
              <button
                className="btn-primary"
                onClick={(e) => {
                  e.stopPropagation()
                  openFilePicker()
                }}
              >
                <UploadCloud size={16} />
                Upload media
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="relative w-full max-w-xs">
                <Search
                  size={16}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
                />
                <input
                  className="input pl-9"
                  placeholder="Search by name…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                {FILTERS.map((f) => {
                  const active = filter === f.key
                  const count =
                    f.key === 'all'
                      ? counts.total
                      : f.key === 'image'
                        ? counts.images
                        : counts.videos
                  return (
                    <button
                      key={f.key}
                      onClick={() => setFilter(f.key)}
                      className={[
                        'chip border transition-colors',
                        active
                          ? 'border-brand-200 bg-brand-50 text-brand-600'
                          : 'border-line text-ink-500 hover:text-ink-900',
                      ].join(' ')}
                    >
                      {f.label}
                      <span className="ml-1.5 text-xs text-ink-400">{count}</span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Grid */}
            {filtered.length === 0 ? (
              <div className="card grid place-items-center py-16 text-center">
                <Search size={28} className="mb-3 text-ink-300" />
                <p className="text-sm text-ink-500">No media matches your search.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
                {filtered.map((item) => (
                  <MediaTile
                    key={item.id}
                    item={item}
                    onDelete={() => removeMedia(item.id)}
                    onRename={(name) => updateMedia(item.id, { name })}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  )
}

function MediaTile({
  item,
  onDelete,
  onRename,
}: {
  item: MediaItem
  onDelete: () => void
  onRename: (name: string) => void
}) {
  const real = isRealUrl(item.url)
  const TypeIcon = item.type === 'video' ? Film : ImageIcon

  const editAlt = () => {
    const next = window.prompt(
      'Name / alt text (sent to networks as the image description)',
      item.name,
    )
    if (next != null && next.trim() && next !== item.name) onRename(next.trim())
  }

  return (
    <div className="group relative aspect-square overflow-hidden rounded-2xl bg-panel ring-1 ring-line animate-scale-in">
      {/* Visual */}
      {real && item.type === 'image' ? (
        <img
          src={item.url}
          alt={item.name}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
      ) : real && item.type === 'video' ? (
        <video
          src={item.url}
          muted
          playsInline
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
      ) : (
        <div
          className="flex h-full w-full flex-col items-center justify-center gap-2 bg-ink-100 p-3 text-center text-ink-500"
        >
          <ImageIcon size={26} />
          <span className="line-clamp-2 text-xs font-medium">Media URL unavailable</span>
        </div>
      )}

      {/* Type badge top-left */}
      <span className="absolute left-2 top-2 grid h-7 w-7 place-items-center rounded-lg bg-black/55 text-white backdrop-blur-sm">
        <TypeIcon size={14} />
      </span>

      {/* Video play hint */}
      {real && item.type === 'video' && (
        <span className="absolute right-2 top-2 rounded-lg bg-black/55 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white backdrop-blur-sm">
          Video
        </span>
      )}

      {/* Hover overlay */}
      <div className="pointer-events-none absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100">
        <div className="pointer-events-auto flex items-end justify-between gap-2 p-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-white" title={item.name}>
              {item.name}
            </p>
            <div className="mt-1 flex items-center gap-1.5 text-[11px] text-white/80">
              <span className="inline-flex items-center gap-1 rounded-md bg-white/15 px-1.5 py-0.5 capitalize">
                <TypeIcon size={11} />
                {item.type}
              </span>
              <span>{timeAgo(item.createdAt)}</span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              onClick={editAlt}
              className="grid h-8 w-8 place-items-center rounded-lg bg-white/15 text-white transition-colors hover:bg-white/25"
              title="Edit name / alt text"
            >
              <Pencil size={14} />
            </button>
            {real && (
              <a
                href={item.url}
                download={item.name}
                onClick={(e) => e.stopPropagation()}
                className="grid h-8 w-8 place-items-center rounded-lg bg-white/15 text-white transition-colors hover:bg-white/25"
                title="Download"
              >
                <Download size={15} />
              </a>
            )}
            <button
              onClick={onDelete}
              className="grid h-8 w-8 place-items-center rounded-lg bg-accent-red/80 text-white transition-colors hover:bg-accent-red"
              title="Delete"
            >
              <Trash2 size={15} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
